/**
 * The route table — every screen, and the guard it sits behind.
 *
 * The old table had fifteen routes, six more screens hidden inside the radar as
 * component state, and three separate shells. Ten of these are real screens
 * with real addresses, all under one shell.
 *
 * The paths changed, so the old ones redirect rather than 404 — bookmarks and
 * anything already sent to a phone keep working.
 */
import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { SessionProvider } from "@/lib/session";
import RequireService from "@/layouts/RequireService";
import AppLayout from "@/layouts/AppLayout";

/* Public */
// The product's own home page. `NovaHome` — a web-agency site that advertised
// websites, e-commerce and "בוטים לוואטסאפ" as one service of five — is left in
// the tree but no longer routed: nothing on it described this business.
const Marketing = lazy(() => import("@/pages/marketing/NedlenHome"));
const Login = lazy(() => import("@/pages/Login"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Invite = lazy(() => import("@/pages/Invite"));

/* The console */
const Home = lazy(() => import("@/pages/Home"));
const Radar = lazy(() => import("@/pages/Radar"));
const Inbox = lazy(() => import("@/pages/dashboard/Inbox"));
const Contacts = lazy(() => import("@/pages/dashboard/Contacts"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const Sources = lazy(() => import("@/pages/Sources"));
const Scans = lazy(() => import("@/pages/Scans"));
const Agent = lazy(() => import("@/pages/Agent"));
const Simulator = lazy(() => import("@/pages/wre/Simulator"));
const Channel = lazy(() => import("@/pages/dashboard/Whatsapp"));
const Geometry = lazy(() => import("@/pages/wre/LocalMap"));
const Settings = lazy(() => import("@/pages/Settings"));
const Admin = lazy(() => import("@/pages/Admin"));

export default function App() {
  return (
    <SessionProvider>
      <Suspense fallback={<div className="state" aria-busy="true" />}>
        <Routes>
          {/* Public. The marketing homepage keeps `/`, so the console lives at
              /home — the same split the reference makes. */}
          <Route path="/" element={<Marketing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/invite/:token" element={<Invite />} />

          {/* The console — one shell, one sidebar, one live connection. */}
          <Route element={<RequireService service="wre" />}>
            <Route element={<AppLayout />}>
              <Route path="/home" element={<Home />} />
              <Route path="/radar" element={<Radar />} />
              {/* The selected apartment is a route, so it survives a refresh
                  and Back closes it instead of leaving the screen. */}
              <Route path="/radar/:id" element={<Radar />} />
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/inbox/:id" element={<Inbox />} />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/sources" element={<Sources />} />
              <Route path="/scans" element={<Scans />} />
              <Route path="/agent" element={<Agent />} />
              <Route path="/simulator" element={<Simulator />} />
              <Route path="/channel" element={<Channel />} />
              <Route path="/geometry" element={<Geometry />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
          </Route>

          {/* Platform admin — the same shell, not a separate world. */}
          <Route element={<RequireService admin />}>
            <Route element={<AppLayout />}>
              <Route path="/admin" element={<Admin />} />
            </Route>
          </Route>

          {/* ── Where the old URLs went ─────────────────────────────────
              `replace` so the dead path does not sit in the history and send
              the back button straight back through the redirect. */}
          <Route path="/dashboard" element={<Navigate to="/home" replace />} />
          <Route path="/dashboard/inbox" element={<Navigate to="/inbox" replace />} />
          <Route path="/dashboard/contacts" element={<Navigate to="/contacts" replace />} />
          <Route path="/dashboard/analytics" element={<Navigate to="/analytics" replace />} />
          <Route path="/dashboard/logs" element={<Navigate to="/analytics?tab=logs" replace />} />
          <Route path="/dashboard/whatsapp" element={<Navigate to="/channel" replace />} />
          <Route path="/dashboard/integrations" element={<Navigate to="/settings?tab=integrations" replace />} />
          <Route path="/dashboard/users" element={<Navigate to="/settings?tab=users" replace />} />
          <Route path="/dashboard/settings" element={<Navigate to="/settings" replace />} />
          <Route path="/wre" element={<Navigate to="/radar" replace />} />
          <Route path="/wre/local-map" element={<Navigate to="/geometry" replace />} />
          <Route path="/wre/simulator" element={<Navigate to="/simulator" replace />} />
          {/* The Google map and the Yad2 mock-up are gone: the basemap is a
              switch on the one map now, and the scanner never scanned. */}
          <Route path="/wre/google-map" element={<Navigate to="/radar" replace />} />
          <Route path="/wre/yad2-scanner" element={<Navigate to="/radar" replace />} />

          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </Suspense>
    </SessionProvider>
  );
}
